import { useState } from 'react';
import {
  Card,
  DataTable,
  EmptyState,
  Select,
  Text,
  Badge,
  Pagination,
  BlockStack,
  InlineStack,
  Spinner,
} from '@shopify/polaris';
import { useAuditLogs } from '../../hooks/useAuditLog';
import { type AuditFilters } from '../../api/audit';

const EVENT_OPTIONS = [
  { label: 'All events',         value: '' },
  { label: 'Shop Installed',     value: 'shop.installed' },
  { label: 'Shop Updated',       value: 'shop.updated' },
  { label: 'Settings Updated',   value: 'settings.updated' },
  { label: 'Sync Triggered',     value: 'sync.triggered' },
  { label: 'Sync Completed',     value: 'sync.completed' },
  { label: 'API Key Created',    value: 'api_key.created' },
  { label: 'API Key Revoked',    value: 'api_key.revoked' },
  { label: 'Plan Changed',       value: 'plan.changed' },
  { label: 'Webhook Received',   value: 'webhook.received' },
];

function eventBadgeTone(event: string): 'success' | 'warning' | 'critical' | 'info' {
  if (event.includes('failed') || event.includes('revoked') || event.includes('deleted')) return 'critical';
  if (event.includes('installed') || event.includes('created') || event.includes('completed')) return 'success';
  if (event.includes('triggered') || event.includes('changed')) return 'warning';
  return 'info';
}

export function AuditLogTable() {
  const [filters, setFilters]     = useState<AuditFilters>({ per_page: 25 });
  const [currentPage, setPage]    = useState(1);

  const { data, isLoading, isError } = useAuditLogs({ ...filters, page: currentPage });

  const rows =
    data?.data.map((log) => [
      <Badge tone={eventBadgeTone(log.event)}>{log.event_label}</Badge>,
      log.auditable_type ?? '—',
      log.ip_address ?? '—',
      new Date(log.created_at).toLocaleString(),
    ]) ?? [];

  if (isError) {
    return (
      <Card>
        <EmptyState
          heading="Unable to load audit logs"
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <p>Try refreshing the page.</p>
        </EmptyState>
      </Card>
    );
  }

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h2">Activity Log</Text>
            <Select
              label="Filter by event"
              labelHidden
              options={EVENT_OPTIONS}
              value={filters.event ?? ''}
              onChange={(value) => {
                setFilters((prev) => ({ ...prev, event: value || undefined }));
                setPage(1);
              }}
            />
          </InlineStack>

          {isLoading ? (
            <InlineStack align="center">
              <Spinner size="small" />
            </InlineStack>
          ) : (
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text']}
              headings={['Event', 'Resource', 'IP Address', 'Timestamp']}
              rows={rows}
              footerContent={
                data && `Showing ${data.data.length} of ${data.meta.total} entries`
              }
            />
          )}
        </BlockStack>
      </Card>

      {data && data.meta.last_page > 1 && (
        <InlineStack align="center">
          <Pagination
            hasPrevious={currentPage > 1}
            onPrevious={() => setPage((p) => p - 1)}
            hasNext={currentPage < data.meta.last_page}
            onNext={() => setPage((p) => p + 1)}
          />
        </InlineStack>
      )}
    </BlockStack>
  );
}
